import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from '../../../assets/pdfMakeFonts/vfs_fonts';
import { HelperService } from 'src/app/services/utils/helper.service';
import { SummaryAuditTransactionSummary } from 'src/app/dataModels/summary-audit-transaction-summary';
import { SessionService } from 'src/app/services/session/session.service';
import { SESSION_KEYS } from 'src/app/dataModels/session-keys';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * PDF export using pdf make
 */
@Injectable({
  providedIn: 'root'
})
export class PdfService {
  /**
   * margin for left align
   */
  PDF_LEFT_ALIGN = 160;

  /**
   * margin for right align
   */
  PDF_RIGHT_ALIGN = 50;

  /**
   * constructor to inject dependency
   * @param helperService instance of helperService
   * @param sessionService instance of sessionService
   */
  constructor(private helperService: HelperService, private sessionService: SessionService) {}

  /**
   * export data to pdf file
   * @param title title of the pdf
   * @param data data to be exported
   * @param headers headers for the data
   * @param additionalData additional data to be included above the data
   * @param subTitle subTitle for the pdf
   * @param fileName fileName for the pdf to be downloaded
   */
  exportTableToPDF(title: string, data: any[], headers: any[], additionalData: any[], subTitle: string, fileName?: string) {
    const user = this.sessionService.getSessionData(SESSION_KEYS.USER);
    const pdfDoc = {} as any;
    const content = [];
    const styles = {
      headerAlt: {
        fontSize: 14,
        bold: true
      },
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10]
      },
      subheader: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 5]
      },
      tableExample: {
        margin: [0, 5, 0, 15],
        fontSize: 9
      },
      tableHeader: {
        bold: false,
        fontSize: 9,
        color: '#333'
      }
    };
    content.push({
      columns: [
        {
          text: this.helperService.formatDate(new Date(), true),
          style: 'tableHeader',
          alignment: 'left'
        },
        {
          text: `Printed By : ${user.lastName}, ${user.firstName}`,
          style: 'tableHeader',
          alignment: 'right'
        }
      ]
    });
    content.push({
      text: `New Jersey Immunization Information System`,
      style: 'headerAlt',
      alignment: 'center'
    });
    if (!this.helperService.IsNullOrEmpty(title)) {
      content.push({
        text: title,
        style: 'headerAlt',
        alignment: 'center'
      });
    }
    if (!this.helperService.IsNullOrEmpty(subTitle)) {
      content.push({
        text: subTitle,
        style: 'headerAlt',
        alignment: 'center'
      });
    }

    if (additionalData.length > 0) {
      const numberOfColumns = this.getNumberOfColumns(additionalData);
      const pdfAdditionalData = [];
      for (const ad of additionalData) {
        const newAd = [];
        if (numberOfColumns === ad.length) {
          Object.keys(ad).forEach((d, i) => {
            newAd[i] = { text: ad[d] };
          });
        } else {
          Object.keys(ad).forEach((d, i) => {
            if (i === ad.length - 1) {
              newAd[i] = { text: ad[d], colSpan: numberOfColumns - ad.length + 1 };
            } else {
              newAd[i] = { text: ad[d] };
            }
          });
        }
        if (newAd.length < numberOfColumns) {
          for (let i = newAd.length; i < numberOfColumns; i++) {
            newAd.push('');
          }
        }
        pdfAdditionalData.push(newAd);
      }
      content.push({
        style: 'tableExample',
        table: {
          body: pdfAdditionalData
        }
      });
    }

    const pdfTableData = [];

    let headerCount = 0;

    const pdfHeaders = [];

    for (const header of headers) {
      if (this.helperService.isHeaderPartOfExport(header)) {
        pdfHeaders.push(header.text);
        headerCount++;
      }
    }
    pdfTableData.push(pdfHeaders);

    for (const d of data) {
      const pdfCells = [];
      for (const h of headers) {
        if (this.helperService.isHeaderPartOfExport(h)) {
          const cellData = h.format === 'PHONE' ? this.helperService.formatPhone(d[h.field]) : d[h.field];
          if (!this.helperService.IsNullOrEmpty(cellData)) {
            pdfCells.push(cellData.toString().replace(/<br\/>/g, '\n'));
          } else {
            pdfCells.push('');
          }
        }
      }
      if (pdfCells.length > 0) {
        pdfTableData.push(pdfCells);
      }
    }

    content.push({
      style: 'tableExample',
      table: {
        headerRows: 1,
        body: pdfTableData
      }
    });

    pdfDoc.pageSize = 'A4';

    pdfDoc.pageOrientation = headerCount > 7 ? 'landscape' : 'portrait';

    pdfDoc.content = content;

    pdfDoc.styles = styles;

    pdfDoc.pageMargins = [20, 30, 20, 10];

    try {
      const date = this.helperService.convertDate(new Date(), 'DD_MMM_YYYY_HH_mm_ss');
      const downloadFileName = fileName || 'Pdf';
      pdfMake.createPdf(pdfDoc).download(`${downloadFileName}_${date}.pdf`);
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * get number of columns the excel has
   * @param additionalData additionalData
   */
  private getNumberOfColumns(additionalData) {
    let numberOfColumns = 0;

    for (const data of additionalData) {
      numberOfColumns = numberOfColumns > data.length ? numberOfColumns : data.length;
    }

    return numberOfColumns;
  }

  /**
   * Export Immunization record information to pdf
   * @param stateSealImageUrl NJIIS state seal image url
   * @param ImmunizationRecordData immunization record data to export
   */
  exportImmunizationRecord(stateSealImageUrl, ImmunizationRecordData) {
    this.getBase64FromImageUrl(stateSealImageUrl, dataURL => {
      this.createImmunizationRecordPDF(dataURL, ImmunizationRecordData);
    });
  }

  /**
   * get base64 of njiis seal
   * @param url image url
   * @param callback callback
   */
  getBase64FromImageUrl(url, callback) {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.onload = e => {
      let canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('base64IMg');
      canvas = null;
      callback(dataURL);
    };
    img.src = url;
  }

  /**
   * create immunization record in PDF
   * @param base64Img base64 image
   * @param immunizationRecordData immunization record data to export
   */
  private createImmunizationRecordPDF(base64Img, immunizationRecordData) {
    const patientImmVaccRecordsForPDF = [['Vaccine', 'Dose 1', 'Dose 2', 'Dose 3', 'Dose 4', 'Dose 5', 'Dose 6', 'Dose 7']];
    for (const vaccine of immunizationRecordData.vaccineRecords) {
      patientImmVaccRecordsForPDF.push([
        vaccine.Vaccine,
        {
          text: vaccine.Date1,
          bold: false
        },
        {
          text: vaccine.Date2,
          bold: false
        },
        {
          text: vaccine.Date3,
          bold: false
        },
        {
          text: vaccine.Date4,
          bold: false
        },
        {
          text: vaccine.Date5,
          bold: false
        },
        {
          text: vaccine.Date6,
          bold: false
        },
        {
          text: vaccine.Date7,
          bold: false
        }
      ]);
    }

    const pdfDoc = {
      pageSize: 'A4',
      content: [
        {
          // header part
          columns: [
            {
              image: base64Img,
              width: 60,
              height: 60
            },
            {
              // start header
              stack: [
                {
                  text: 'New Jersey Department of Health',
                  style: 'header'
                },
                {
                  text: 'PERSONAL IMMUNIZATION RECORD',
                  style: 'header'
                },
                {
                  text: '-OFFICIAL DOCUMENT-',
                  style: 'centerAlign'
                },
                {
                  text: 'REGISTRO DE IMMUNIZACION',
                  style: 'centerAlign'
                },
                {
                  table: {
                    body: [
                      [
                        {
                          stack: [
                            {
                              text: "CHILDREN MUST MEET NEW JERSEY'S IMMUNIZATION",
                              style: 'centerAlignSmall'
                            },
                            {
                              text: [
                                'REQUIREMENTS TO BE ENROLLED IN SCHOOL.',
                                {
                                  text: 'THIS IS THE LAW !',
                                  bold: true
                                }
                              ],
                              style: 'centerAlignSmall'
                            }
                          ]
                        }
                      ] // table columns
                    ] // end of body
                  },
                  style: 'tablecenter'
                }
              ]
            } // end header
          ] // end of columns
        },
        // start body content
        {
          style: 'marginTop',
          columns: [
            {
              width: 100,
              text: [
                'Name\n',
                {
                  text: 'nombre',
                  italics: true
                }
              ]
            },
            {
              width: 150,
              text: immunizationRecordData.name,
              decoration: 'underline',
              fontSize: '9',
              bold: true
            },
            {
              width: 200,
              text: [
                'Birthdate (mm/dd/yyy)\n',
                {
                  text: 'fecha de nacimeinto',
                  italics: true
                }
              ]
            },
            {
              text: immunizationRecordData.dateOfBirth,
              decoration: 'underline',
              fontSize: '9',
              bold: true
            }
          ] // end of columns
        },
        {
          style: 'marginTop',
          columns: [
            {
              width: 100,
              text: ['Registry ID']
            },
            {
              width: 150,
              text: immunizationRecordData.registryId,
              decoration: 'underline',
              fontSize: '9',
              bold: true
            },
            {
              width: 200,
              text: [
                'Sex\n',
                {
                  text: 'sexo',
                  italics: true
                }
              ]
            },
            {
              text: immunizationRecordData.gender,
              decoration: 'underline',
              fontSize: '9',
              bold: true
            }
          ] // end of columns
        },
        {
          text: 'DO NOT WRITE ON THIS FORM. THIS IS AN OFFICIAL IMMUNIZATION DOCUMENT.',
          bold: true,
          fontSize: '9',
          alignment: 'center'
        },
        {
          style: 'tableHeader',
          table: {
            widths: [118, 48, 48, 48, 48, 48, 48, 48],
            headerRows: 1,
            body: patientImmVaccRecordsForPDF
          }
        },
        {
          text: [
            {
              text: 'Important Notice :',
              bold: true
            },
            'This record displays doses given and recorded in the NJIIS, whether dose(s) are considered valid or invalid. The record reflects the last TB and Lead Screening recorded in the NJIIS.'
          ],
          style: 'marginTop'
        },
        {
          style: 'tableHeader',
          table: {
            widths: [150, 270, 80],
            body: [
              ['', 'Result', 'Test Date'],
              [
                'Lead',
                {
                  text: immunizationRecordData.leadResult,
                  bold: false
                },
                {
                  text: immunizationRecordData.leadResultDate,
                  bold: false
                }
              ],
              [
                'TB',
                {
                  text: immunizationRecordData.tbResult,
                  bold: false
                },
                {
                  text: immunizationRecordData.tbResultDate,
                  bold: false
                }
              ],
              [
                'Varicella (Chicken Pox)',
                {
                  text: immunizationRecordData.letResult,
                  bold: false
                },
                {
                  text: immunizationRecordData.letResultDate,
                  bold: false
                }
              ],
              [
                'Measles',
                {
                  text: immunizationRecordData.measlesResult,
                  bold: false
                },
                {
                  text: immunizationRecordData.measlesResultDate,
                  bold: false
                }
              ]
            ]
          }
        },
        {
          text:
            'The information contained here is obtained from the registry and shall be accepted as a valid immunization record for the purpose of meeting immunization requirements for admission to schools, colleges, licensed child care centers, or other institutions, when signed and dated, by the authorized healthcare provider.',
          style: 'marginTop'
        },
        {
          style: 'marginTopExtra',
          columns: [
            {
              width: 408,
              text: '_______________________________________'
            },
            {
              text: immunizationRecordData.todaysDate,
              bold: true,
              decoration: 'underline',
              fontSize: '9'
            }
          ]
        },
        {
          columns: [
            {
              width: 408,
              text: 'Signature of the Person Issuing Record',
              fontSize: '9'
            },
            {
              text: 'Date',
              fontSize: '9'
            }
          ]
        },
        {
          style: 'marginTop',
          columns: [
            {
              width: 408,
              text: immunizationRecordData.facilityName,
              bold: true,
              decoration: 'underline',
              fontSize: '9'
            },

            {
              text: immunizationRecordData.userName,
              bold: true,
              decoration: 'underline',
              fontSize: '9'
            }
          ]
        },
        {
          columns: [
            {
              width: 408,
              text: 'Agency/Office Issuing Record',
              fontSize: '9'
            },
            {
              text: 'Registry User Name',
              fontSize: '9'
            }
          ]
        }
      ], // end of content
      styles: {
        header: {
          fontSize: 9,
          bold: true,
          margin: [0, 0, 0, 0],
          alignment: 'center'
        },
        centerAlign: {
          fontSize: 9,
          margin: [0, 0, 0, 0],
          alignment: 'center'
        },
        centerAlignSmall: {
          fontSize: 9,
          margin: [0, 0, 0, 0],
          alignment: 'center'
        },
        tablecenter: {
          alignment: 'center',
          margin: [90, 0, 0, 0]
        },
        marginTop: {
          margin: [0, 5, 0, 0],
          fontSize: 9
        },
        marginTopExtra: {
          margin: [0, 10, 0, 0]
        },
        marginRightBoldUnderLine: {
          margin: [0, 0, 0, 10],
          bold: true,
          decoration: 'underline'
        },
        marginRight: {
          margin: [200, 0, 0, 0]
        },
        tableHeader: {
          bold: true,
          fontSize: 9,
          color: '#333'
        }
      } // end of styles
    }; // end of pdf doc obj

    pdfMake.createPdf(pdfDoc).download('NJIIS_Official_Immunization_Record.pdf');
  }

  /**
   * html to pdf
   * @param htmlText html content to be converted to pdf
   * @param fileName fileName to be downloaded
   */
  htmlToPDF(htmlText, fileName) {
    const date = this.helperService.convertDate(new Date(), 'DD_MMM_YYYY_HH_mm_ss');
    const pdfDoc = {} as any;
    const content = [];
    const styles = {
      symbol: {
        font: 'FontAwesome',
        fontSize: 9,
        margin: [0, -20, 0, 0],
        alignment: 'center'
      },
      symbol1: {
        font: 'FontAwesome',
        fontSize: 9,
        margin: [0, -20, 0, 0],
        alignment: 'center'
      },
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10]
      },
      subheader: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 5]
      },
      tableNoMargin: {
        bold: false,
        fontSize: 9,
        color: '#333',
        width: '560',
        margin: [0, -12, 0, 0]
      },
      tableHeader: {
        bold: false,
        fontSize: 9,
        color: '#333',
        width: '560'
      }
    };
    this.parseHtml(content, htmlText);
    pdfDoc.pageSize = 'A4';
    pdfDoc.content = content;
    pdfDoc.styles = styles;
    pdfDoc.pageMargins = [20, 30, 20, 10];
    pdfMake.fonts = {
      FontAwesome: {
        normal: 'fa-regular-400.ttf',
        bold: 'fa-regular-400.ttf',
        italics: 'fa-regular-400.ttf',
        bolditalics: 'fa-regular-400.ttf'
      },
      Roboto: {
        normal: 'Roboto-Regular.ttf',  // Path to your regular font
        bold: 'Roboto-Regular.ttf',       // Path to your bold font
        italics: 'Roboto-Italic.ttf',  // Path to your italic font
        bolditalics: 'Roboto-Italic.ttf'  // Path to your bold italic font
      }
    };
    pdfMake.createPdf(pdfDoc).download('AudiologistReleaseOfInformation_' + date + '.pdf');
  }

  /**
   * print NBH Followup Auth Form
   * @param physicianInfo physician information
   * @param patientInfo patient information
   */
  printEHDINBHFollowupAuthForm(physicianInfo, patientInfo) {
    const facilityPhoneNumber = this.helperService.formatPhone(physicianInfo.userFacilityPhoneNumber);
    const authHtml = `<table>
        <tr>
            <td>
                <span style="font-size:14px; font-weight:bold; text-align:center">Authorization to Release Information to the New Jersey Early Intervention System</span>
            </td>
        </tr>
    </table>
    <table border="1">
        <tr>
            <td colspan="3">
                <strong>Audiologist/Facility Name</strong>
                <br /> ${physicianInfo.userFacilityName}</td>
        </tr>
        <tr>
            <td>
                <strong>Street Address</strong>
                <br /> ${physicianInfo.userFacilityAddress}</td>
            <td>
                <strong>City, State, Zip Code</strong>
                <br /> ${physicianInfo.userFacilityCity}, ${physicianInfo.userFacilityState}, ${physicianInfo.userFacilityZip}
            </td>
            <td>
                <strong>Office Contact Number</strong>
                <br /> ${facilityPhoneNumber}</td>
        </tr>
        <tr>
            <td>
                <strong>Child’s First Name</strong>
                <br /> ${patientInfo.firstName}</td>
            <td>
                <strong>Child’s Last Name</strong>
                <br /> ${patientInfo.lastName}</td>
            <td>
                <strong>Child’s DOB</strong>
                <br /> ${patientInfo.dateOfBirth}</td>
        </tr>
        <tr>
            <td colspan="2">
                <strong>Parent's Name</strong>
                <br /> ${patientInfo.relativeName}</td>
            <td>
                <table tableStyle="tableNoMargin">
                    <tr>
                        <td colspan="2">
                            <strong>Contact Number </strong>
                        </td>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Home</td>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Work</td>
                        <td>
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Mobile</td>
                    </tr>
                </table>
                <br /> ${patientInfo.phoneNumber || ''}
            </td>
        </tr>
        <tr>
            <td>
                <strong>Street Address</strong>
                <br /> ${patientInfo.streetAddress}</td>
            <td>
                <strong>City, State, Zip Code</strong>
                <br /> ${patientInfo.city}, ${patientInfo.birthState}, ${patientInfo.zip}
            </td>
            <td>
                <strong>Email Address</strong>
                <br /> </td>
        </tr>
        <tr>
            <td colspan="3">
                <strong>Purpose for Releasing Records</strong>
                <p style="text-align:justify">To provide parents of young children recently found to have a hearing loss access to referral to the New Jersey Early Intervention System (NJEIS. Parental consent is being requested to release information to the New Jersey Early Intervention
                    System (NJEIS) for your child with a hearing loss. The NJEIS provides services to children with developmental delays and disabilities, from birth to age three years, and their families. This release form and the disclosed documentation
                    you agree to release will be part of your child’s record maintained at the NJEIS Regional System Point of Entry (SPOE).</p>
                <p style="text-align:justify">The NJEIS is required to consider receipt of this information as a referral to early intervention. However, you are under no obligation to accept services from NJEIS. You may decline any services the NJEIS offers you for your child or
                    family. The NJEIS has a professional Hearing Consultant who has worked with children with hearing loss that can be made available to talk with you and answer questions you may have about the impact of your child’s hearing loss, the
                    choices you have in helping your child communicate and what the NJEIS has to offer your child and family.
                </p>
            </td>
        </tr>
        <tr>
            <td colspan="3">
                <strong>Check below the documents that you agree to release to the NJEIS:</strong>
                <br />
                <table widths="2%,98%" tableStyle="tableNoMargin">
                    <tr>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Diagnostic audiology reports for your child.</td>
                    </tr>
                    <tr>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Audiologist’s test results and recommendations for your child for future visits covered by this consent.</td>
                    </tr>
                </table>
            </td>
        </tr>
        <tr>
            <td colspan="3">
                <strong>Information will be faxed to the following Regional Early Intervention Collaborative System
                    Point of
                    Entry:</strong>
                <table widths="2%,98%" tableStyle="tableNoMargin">
                    <tr>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Family Link, Inc. REIC (Essex, Morris, Sussex, Union, Warren) @ (908) 964-6091
                        </td>
                    </tr>
                    <tr>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td>Mid-Jersey REIC (Hunterdon, Mercer, Middlesex, Monmouth, Ocean, Somerset) @ (732) 937-5540
                        </td>
                    </tr>
                    <tr>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Northeast REIC (Bergen, Hudson, Passaic) @ (973) 256-1233</td>
                    </tr>
                    <tr>
                        <td text-align="left">
                            <htmlcheckbox></htmlcheckbox>
                        </td>
                        <td text-align="left">Southern NJ REIC (Atlantic, Burlington, Camden, Cape May, Cumberland, Gloucester, Salem) @ (856) 768-2059</td>
                    </tr>
                </table>
            </td>
        </tr>
        <tr>
            <td colspan="3">
                <strong>I understand that:</strong>
                <div>1. Transmission of this information with my consent will be considered a referral to NJEIS.</div>
                <div>2. If my child is referred to NJEIS, my family will have no obligation to accept services under NJEIS.
                </div>
                <div>3. Services I get from my audiologist will not be affected if I choose not to consent to release information to NJEIS.</div>
                <div>4. I may change or withdraw this consent to release my child’s records at any time and that this authorization shall expire one year from the date this consent was signed;</div>
                <div>5. If I change my mind after signing this form and do not wish to authorize the release of this information, I must send a written notice to the audiologist or facility indicated above; and</div>
                <div>6. The withdrawal of this consent will be valid as soon as the person or facility gets the notice, but will not apply to information that has already been shared after you sign this form.</div>
            </td>
        </tr>
        <tr>
            <td colspan="2">
                <strong>Parent/Guardian Signature</strong>
                <br />
            </td>
            <td>
                <strong>Date</strong>
                <br />
            </td>
        </tr>
    </table>`;

    this.htmlToPDF(authHtml, 'AudiologistReleaseOfInformation_');
  }

  /**
   * parse html to pdf content
   * @param cnt pdf content
   * @param htmlText html text
   */
  parseHtml(cnt, htmlText) {
    const divElem = document.createElement('div');
    divElem.innerHTML = htmlText.replace(/\t/g, '').replace(/\n/g, '');
    const p = this.createParagraph();
    for (let i = 0; i < divElem.childNodes.length; i++) {
      this.parseElement(cnt, divElem.childNodes[i], p);
    }
  }

  /**
   * find symbol for class
   * @param selector selector
   */
  findSymbolForClass(selector) {
    let result = '';
    const sheets = document.styleSheets;

    for (let sheetNr = 0; sheetNr < sheets.length; sheetNr++) {
      const content = this.findCSSRuleContent(sheets[sheetNr], selector);

      if (content) {
        result = this.stripQuotes(content);
        break;
      }
    }
    return result;
  }

  /**
   * find css rule
   * @param mySheet sheet
   * @param selector selector
   */
  findCSSRuleContent(mySheet, selector) {
    let ruleContent = '';
    const rules = mySheet.cssRules ? mySheet.cssRules : mySheet.rules;

    for (let i = 0; i < rules.length; i++) {
      const text = rules[i].selectorText;
      if (text && text.indexOf(selector) >= 0) {
        ruleContent = rules[i].style.content;
        break;
      }
    }

    return ruleContent;
  }

  /**
   * strip quotes from start and end of the string
   * @param value value
   */
  stripQuotes(value) {
    const len = value.length;
    return value.slice(1, len - 1);
  }

  /**
   * add element to the content
   * @param cnt content of pdf
   * @param e element
   * @param p paragraph of pdf
   * @param styles styles for the element
   */
  parseContainer(cnt, e, p, styles = []) {
    const elements = [];
    const children = e.childNodes;
    if (children.length != 0) {
      for (let i = 0; i < children.length; i++) {
        p = this.parseElement(elements, children[i], p, styles);
      }
    }
    if (elements.length != 0) {
      for (let i = 0; i < elements.length; i++) {
        cnt.push(elements[i]);
      }
    }
    return p;
  }

  /**
   * computes style for element in pdf format
   * @param o element
   * @param styles styles to be applied to element
   */
  computeStyle(o, styles) {
    for (let i = 0; i < styles.length; i++) {
      const st = styles[i]
        .trim()
        .toLowerCase()
        .split(':');
      if (st.length === 2) {
        switch (st[0]) {
          case 'font-size': {
            o.fontSize = parseInt(st[1], 10);
            break;
          }
          case 'text-align': {
            switch (st[1]) {
              case 'right':
                o.alignment = 'right';
                break;
              case 'center':
                o.alignment = 'center';
                break;
              case 'left':
                o.alignment = 'left';
                break;
            }
            break;
          }
          case 'font-weight': {
            switch (st[1]) {
              case 'bold':
                o.bold = true;
                break;
            }
            break;
          }
          case 'text-decoration': {
            switch (st[1]) {
              case 'underline':
                o.decoration = 'underline';
                break;
            }
            break;
          }
          case 'font-style': {
            switch (st[1]) {
              case 'italic':
                o.italics = true;
                break;
            }
            break;
          }
        }
      }
    }
  }

  /**
   * parse the html element, and append to pdf content
   * @param cnt content of pdf
   * @param e element
   * @param p paragraph of pdf
   * @param styles styles to be applied
   */
  parseElement(cnt, e, p, styles = []) {
    if (!styles) {
      styles = [];
    }
    if (e.getAttribute) {
      const nodeStyle = e.getAttribute('style');
      if (nodeStyle) {
        const ns = nodeStyle.split(';');
        for (let k = 0; k < ns.length; k++) {
          styles.push(ns[k]);
        }
      }
    }

    switch (e.nodeName.toLowerCase()) {
      case '#text': {
        const t = {
          text: e.textContent.replace(/\n/g, '')
        } as any;
        if (styles) {
          this.computeStyle(t, styles);
        }
        p.text.push(t);
        break;
      }
      case 'b':
      case 'strong': {
        // styles.push("font-weight:bold");
        this.parseContainer(cnt, e, p, styles.concat(['font-weight:bold']));
        break;
      }
      case 'u': {
        // styles.push("text-decoration:underline");
        this.parseContainer(cnt, e, p, styles.concat(['text-decoration:underline']));
        break;
      }
      case 'i': {
        // styles.push("font-style:italic");
        this.parseContainer(cnt, e, p, styles.concat(['font-style:italic']));
        // styles.pop();
        break;
        // cnt.push({ text: e.innerText, bold: false });
      }
      case 'span': {
        this.parseContainer(cnt, e, p, styles);
        break;
      }
      case 'br': {
        p = this.createParagraph();
        cnt.push(p);
        break;
      }
      case 'table': {
        styles = [];
        const t = {
          style: e.getAttribute('tableStyle') ? e.getAttribute('tableStyle') : 'tableHeader',
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [],
            body: []
          }
        } as any;
        const border = e.getAttribute('border');
        let isBorder = false;
        if (border) {
          if (parseInt(border, 10) === 1) {
            isBorder = true;
          }
        }
        if (!isBorder) {
          t.layout = 'noBorders';
        }
        this.parseContainer(t.table.body, e, p, styles);

        const widths = e.getAttribute('widths');
        if (!widths) {
          if (t.table.body.length !== 0) {
            if (t.table.body[0].length !== 0) {
              for (let k = 0; k < t.table.body[0].length; k++) {
                if (k === 0) {
                  t.table.widths.push('auto');
                } else {
                  t.table.widths.push('auto');
                }
              }
            }
          }
        } else {
          const w = widths.split(',');
          for (let k = 0; k < w.length; k++) {
            t.table.widths.push(w[k]);
          }
        }
        cnt.push(t);
        break;
      }
      case 'thead':
      case 'tbody': {
        this.parseContainer(cnt, e, p, styles);
        // p = CreateParagraph();
        break;
      }
      case 'tr': {
        const row = [];
        this.parseContainer(row, e, p, styles);
        cnt.push(row);
        break;
      }
      case 'th':
      case 'td':
      case 'pre': {
        p = this.createParagraph();
        const st = {
          stack: []
        } as any;
        st.stack.push(p);

        const rspan = e.getAttribute('rowspan');
        if (rspan) {
          st.rowSpan = parseInt(rspan, 10);
        }
        const cspan = e.getAttribute('colspan');
        if (cspan) {
          st.colSpan = parseInt(cspan, 10);
        }

        if (e.nodeName.toLowerCase() === 'th') {
          this.parseContainer(st.stack, e, p, styles.concat(['font-weight:bold']));
        } else {
          this.parseContainer(st.stack, e, p, styles.concat(['font-weight:normal']));
        }

        cnt.push(st);

        for (let i = 1; i < cspan; i++) {
          cnt.push({});
        }
        break;
      }
      case 'div':
      case 'p': {
        p = this.createParagraph();
        const st = {
          stack: []
        };
        st.stack.push(p);
        this.computeStyle(st, styles);
        this.parseContainer(st.stack, e, p);

        cnt.push(st);
        break;
      }
      case 'htmlcheckbox': {
        cnt.push({
          stack: [
            {
              text: this.findSymbolForClass('fa-square'),
              style: 'symbol'
            }
          ]
        });
        break;
      }
      case 'htmlcheckbox1': {
        cnt.push({
          stack: [
            {
              text: this.findSymbolForClass('fa-square'),
              style: 'symbol1'
            }
          ]
        });
        break;
      }
      default: {
        console.log('Parsing for node ' + e.nodeName + ' not found');
        break;
      }
    }
    return p;
  }

  /**
   * creates paragrapgh for the pdf
   */
  createParagraph() {
    const p = {
      text: []
    };
    return p;
  }

  /**
   * export transaction summary
   * @param transactionInfo transaction info
   * @param facilityInfo facility information
   * @param searchParams audit search params
   */
  exportTransactionSummary(transactionInfo: SummaryAuditTransactionSummary, facilityInfo, searchParams) {
    const pdfDoc = {
      pageSize: 'A4',
      content: [],
      pageOrientation: 'landscape',
      // styles begin
      styles: {
        header: {
          fontSize: 12,
          bold: true,
          margin: [0, 0, 0, 0],
          alignment: 'center'
        },
        subheader: {
          fontSize: 11,
          bold: true,
          margin: [0, 0, 0, 0],
          alignment: 'center'
        },
        centerAlign: {
          fontSize: 9,
          margin: [0, 0, 0, 0],
          alignment: 'center'
        },
        centerAlignSmall: {
          fontSize: 9,
          margin: [0, 0, 0, 0],
          alignment: 'center'
        },
        tablecenter: {
          alignment: 'center',
          margin: [90, 0, 0, 0]
        },
        marginTop: {
          margin: [0, 5, 0, 0],
          fontSize: 9
        },
        marginTopExtra: {
          margin: [0, 10, 0, 0],
          fontSize: 9
        },
        marginRightBoldUnderLine: {
          margin: [0, 0, 0, 10],
          bold: true,
          decoration: 'underline'
        },
        marginRight: {
          margin: [200, 0, 0, 0]
        },
        appFontSize: {
          fontSize: 9
        }
      } //end of styles
    };

    pdfDoc.content.push(this.pushHeader());
    pdfDoc.content.push(this.pushSummaryInfo(facilityInfo, searchParams));
    pdfDoc.content.push(this.pushTotalRecordsInfo(transactionInfo));
    pdfDoc.content.push(this.pushPatientRecordsInfo(transactionInfo));
    pdfDoc.content.push(this.pushDosesCountInfo(transactionInfo));
    pdfDoc.content.push(this.pushVFCDosesCountInfo(transactionInfo));

    pdfMake.createPdf(pdfDoc).download('Summary_Audit_Report_Transaction_Summary_Details.pdf');
  }

  /**
   * header fir the PDf doc
   */
  private pushHeader() {
    return {
      columns: [
        {
          stack: [
            {
              text: 'New Jersey Immunization Information System',
              style: 'header'
            },
            {
              text: 'Summary Audit Report - Transaction Summary',
              style: 'subheader'
            }
          ] // stack ends
        }
      ]
    };
  }

  /**
   * Append Summary information to the pdf
   * @param facilityInfo facility information
   * @param searchParams Audit tool search params
   */
  private pushSummaryInfo(facilityInfo, searchParams) {
    const summaryInfoArr = [];

    summaryInfoArr.push({
      style: 'marginTopExtra',
      columns: [
        {
          width: 80,
          text: 'Facility Name/Id : ',
          bold: true
        },
        {
          width: 300,
          text: [facilityInfo.facilityName, ' ( ', facilityInfo.facilityId, ' ) '],
          bold: true,
          alignment: 'left'
        },
        {
          width: 50,
          text: 'Start Date :',
          bold: true,
          alignment: 'left'
        },
        {
          width: 60,
          text: this.helperService.formatDate(searchParams.auditStartDate),
          bold: true,
          alignment: 'left'
        },
        {
          width: 50,
          text: 'End Date :',
          bold: true,
          alignment: 'left'
        },
        {
          width: 60,
          text: this.helperService.formatDate(searchParams.auditEndDate),
          bold: true,
          alignment: 'left'
        }
      ]
    });
    return summaryInfoArr;
  }

  /**
   * push Transaction records count Information
   * @param transactionInfo tranaction info
   */

  private pushTotalRecordsInfo(transactionInfo: SummaryAuditTransactionSummary) {
    const totalRecordsArr = [];
    totalRecordsArr.push({
      style: 'marginTopExtra',
      columns: [
        {
          text: 'Records Count',
          decoration: 'underline',
          bold: true
        }
      ]
    });

    totalRecordsArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Total Records : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.TOTAL_RECORDS
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Records Accepted : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.RECORDS_ACCEPTED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Records Rejected : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.RECORDS_REJECTED
        }
      ]
    });

    totalRecordsArr.push({
      style: 'appFontSize',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Total Errors : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.TOTAL_ERRORS
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Fatal / Non Fatal Errors : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: [transactionInfo.FATAL_ERRORS, ' / ', transactionInfo.NON_FATAL_ERRORS]
        }
      ]
    });

    return totalRecordsArr;
  }

  /**
   * push Patients Record Information
   * @param transactionInfo tranaction info
   */
  private pushPatientRecordsInfo(transactionInfo: SummaryAuditTransactionSummary) {
    const patientsArr = [];
    patientsArr.push({
      style: 'marginTopExtra',
      columns: [
        {
          text: 'Patients Count',
          decoration: 'underline',
          bold: true
        }
      ]
    });
    patientsArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Total Patients : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.TOTAL_PATIENTS
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Patients Rejected : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.PATIENTS_REJECTED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Patients Matched : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.PATIENTS_MATCHED
        }
      ]
    });
    patientsArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Patients Added : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.PATIENTS_ADDED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Patients Not Added : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.PATIENTS_NOT_ADDED
        }
      ]
    });

    return patientsArr;
  }

  /**
   * push Doses Count Information
   * @param transactionInfo tranaction info
   */
  private pushDosesCountInfo(transactionInfo: SummaryAuditTransactionSummary) {
    const dosesCountArr = [];
    dosesCountArr.push({
      style: 'marginTopExtra',
      columns: [
        {
          text: 'Doses Count',
          decoration: 'underline',
          bold: true
        }
      ]
    });
    dosesCountArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Total Doses : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.TOTAL_DOSES
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Doses Accepted : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DOSES_ACCEPTED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Doses Rejected : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DOSES_REJECTED
        }
      ]
    });
    dosesCountArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Doses Added : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DOSES_ADDED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Doses Updated : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DOSES_UPDATED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Doses Deleted : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DOSES_DELETED
        }
      ]
    });
    dosesCountArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Duplicate Doses : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DUPLICATE_DOSES
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Doses Added Matched Inventory: ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DOSES_ADDED_INV_MATCH
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Doses Updated Matched Inventory: ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.DOSES_UPDATED_INV_MATCH
        }
      ]
    });

    return dosesCountArr;
  }

  /**
   * push VFC Doses Count Information
   * @param transactionInfo tranaction info
   */
  private pushVFCDosesCountInfo(transactionInfo: SummaryAuditTransactionSummary) {
    const vfcDosesCountArr = [];
    vfcDosesCountArr.push({
      style: 'marginTopExtra',
      columns: [
        {
          text: 'VFC Doses Count',
          decoration: 'underline',
          bold: true
        }
      ]
    });
    vfcDosesCountArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'Total VFC Doses : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.TOTAL_VFC_DOSES
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Doses Accepted : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DOSES_ACCEPTED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Doses Rejected : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DOSES_REJECTED
        }
      ]
    });
    vfcDosesCountArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Doses Added : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DOSES_ADDED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Doses Updated : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DOSES_UPDATED
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Doses Deleted : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DOSES_DELETED
        }
      ]
    });
    vfcDosesCountArr.push({
      style: 'marginTop',
      columns: [
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Duplicate Doses : ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DUPLICATE_DOSES
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Doses Added Matched Inventory: ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DOSES_ADDED_INV_MATCH
        },
        {
          width: this.PDF_LEFT_ALIGN,
          text: 'VFC Doses Updated Matched Inventory: ',
          alignment: 'right'
        },
        {
          width: this.PDF_RIGHT_ALIGN,
          text: transactionInfo.VFC_DOSES_UPDATED_INV_MATCH
        }
      ]
    });

    return vfcDosesCountArr;
  }

  /**
   * Reminder Recall letter for patients
   * @param patientList list of patients
   * @param letterContent content for the letter
   * @param facilityInfo facility information
   * @param facilityName name of facility
   */
  exportReminderRecallLetter(patientList, letterContent, facilityInfo, facilityName) {
    const pdfDoc = {} as any;

    pdfDoc.pageSize = 'A4';

    pdfDoc.content = [];

    pdfDoc.styles = {
      default: {
        fontSize: 10
      },
      headerLeftAlign: {
        fontSize: 10,
        bold: true,
        margin: [0, 0, 0, 0],
        alignment: 'left'
      },
      marginTop: {
        margin: [0, 10, 0, 0],
        fontSize: 10
      },
      tableHeader: {
        bold: true,
        fontSize: 10,
        color: '#333'
      }
    }; // end of styles

    patientList.forEach((patient, i) => {
      const dosesToVaccinateArr = patient.dosesArr.split('~');

      const dosesTableToPrint = [];

      dosesTableToPrint.push([
        {
          text: 'Immunization',
          style: 'tableHeader'
        },
        {
          text: 'Due Date',
          style: 'tableHeader'
        }
      ]);

      for (const dose of dosesToVaccinateArr) {
        const dosesAndDateArr = dose.split('@');

        const cells = [];
        cells[0] = {
          text: dosesAndDateArr[0] || '',
          style: 'default'
        };
        cells[1] = {
          text: dosesAndDateArr[1] || '',
          style: 'default'
        };

        dosesTableToPrint.push(cells);
      }

      const content = [
        {
          columns: [
            {
              stack: [
                {
                  text: 'From',
                  style: 'headerLeftAlign'
                },
                {
                  text: facilityName,
                  style: 'headerLeftAlign'
                },
                {
                  text:
                    facilityInfo.address.addressLine1 + ' ' + facilityInfo.address.addressLine2 + ' ' + facilityInfo.address.addressLine3,
                  style: 'headerLeftAlign'
                },
                {
                  text: facilityInfo.address.city + ', ' + facilityInfo.address.state,
                  style: 'headerLeftAlign'
                },
                {
                  text: this.helperService.formatPhone(facilityInfo.phoneNumber),
                  style: 'headerLeftAlign'
                }
              ]
            }
          ]
        },
        {
          style: 'marginTop',
          columns: [
            {
              width: 310,
              style: 'default',
              text: 'Date : ' + this.helperService.formatDate(new Date())
            },
            {
              width: '*',
              stack: [
                {
                  style: 'default',
                  columns: [
                    {
                      width: 75,
                      text: 'Registry Id:',
                      bold: true
                    },
                    {
                      text: patient.registryId
                    }
                  ]
                },
                {
                  style: 'default',
                  columns: [
                    {
                      width: 75,
                      text: 'Patient Name:',
                      bold: true
                    },
                    {
                      text: patient.firstName + ' ' + patient.middleName + ' ' + patient.lastName
                    }
                  ]
                },
                {
                  style: 'default',
                  columns: [
                    {
                      width: 75,
                      text: 'Date Of Birth:',
                      bold: true
                    },
                    {
                      text: patient.dateOfBirth
                    }
                  ]
                },
                {
                  style: 'default',
                  columns: [
                    {
                      width: 75,
                      text: 'Age:',
                      bold: true
                    },
                    {
                      text: this.helperService.formatAge(this.helperService.calculateAge(patient.dateOfBirth))
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          stack: [
            {
              style: 'default',
              text: 'Dear Parent or Guardian,'
            },
            {
              style: 'marginTop',
              text: letterContent
            },
            {
              style: 'marginTop',
              text: 'The following immunizations are due for ' + patient.firstName + ' ' + patient.middleName + ' ' + patient.lastName
            },
            {
              table: {
                widths: ['50%', '50%'],
                headerRows: 1,
                body: dosesTableToPrint
              }
            },
            {
              style: 'marginTop',
              text: 'If you have any questions or if you think our records may not be up-to-date, please let us know.'
            },
            {
              style: 'marginTop',
              text: 'Thank You',
              pageBreak: i + 1 < patientList.length ? 'after' : null
            }
          ]
        }
      ];

      pdfDoc.content.push(content);
    });

    pdfMake.createPdf(pdfDoc).download('Reminder/Recall Letter.pdf');
  }
}
